import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase'; // Adjusted path
// import {Platform} from 'react-native'; // Removed for web
import {decode} from 'base64-arraybuffer';
import {normalizeUserContent} from '../../utils/securityUtils'; // Adjusted path

interface Location {
  latitude?: number;
  longitude?: number;
  name?: string;
}

interface Photo {
  id?: string;
  uri: string;
  isLocal?: boolean;
}

interface EmailPreferences {
  marketing: boolean;
  product_updates: boolean;
  matches: boolean;
  messages: boolean;
}

export interface DiscoveryPreferences {
  age_range: {
    min: number;
    max: number;
    enabled: boolean;
  };
  height_preference: {
    values: string[];
    enabled: boolean;
  };
  religion_preference: {
    values: string[];
    enabled: boolean;
  };
  has_kids_preference: {
    values: string[];
    enabled: boolean;
  };
  verified_only: boolean;
  location_preference: 'same_country' | 'nearby' | 'regional' | 'global';
}

export interface Profile {
  id?: string;
  full_name?: string;
  email?: string;
  gender?: string;
  birth_date?: string;
  bio?: string;
  preferences?: any; // Consider defining a more specific type if possible
  subscription_status?: string;
  subscription_end_date?: string;
  created_at?: string;
  updated_at?: string;
  sexual_orientation?: string;
  preference?: string;
  photos?: Photo[];
  location?: Location;
  primary_photo_index?: number;
  has_kids?: string;
  is_religious?: string;
  height?: string;
  style?: string;
  willing_to_relocate?: string;
  partner_qualities?: string[];
  industry?: string;
  deal_breakers?: string[];
  email_preferences?: EmailPreferences;
  discovery_preferences?: DiscoveryPreferences;
  is_incognito?: boolean;
  ip_country?: string;
  is_verified?: boolean;
  verified_at?: string;
  verification_expires_at?: string;
}

interface ProfileState {
  data: Profile | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  data: null,
  loading: false,
  error: null,
};

const getFileInfo = (uri: string) => {
  const uriParts = uri.split('/');
  const fileName = uriParts[uriParts.length - 1];
  const fileNameParts = fileName.split('.');
  const extension =
    fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : 'jpg';
  return {fileName, extension};
};

export const fetchProfile = createAsyncThunk<
  Profile,
  void,
  {rejectValue: string}
>('profile/fetchProfile', async (_, {rejectWithValue}) => {
  try {
    const {
      data: {user},
      error: userError,
    } = await supabase.auth.getUser();

    if (userError) {
      throw new Error(`Authentication error: ${userError.message}`);
    }

    if (!user) {
      throw new Error('User not authenticated');
    }

    let data = null;
    let error = null;

    // Retry logic (optional, but kept from original)
    for (let attempt = 1; attempt <= 3; attempt++) {
      const response = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      error = response.error;
      data = response.data;

      if (!error) {
        break;
      }

      if (attempt < 3) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }

    if (error) {
      throw new Error(`Failed to fetch profile: ${error.message}`);
    }

    // Ensure photos are in the correct format
    if (data && data.photos && Array.isArray(data.photos)) {
      data.photos = data.photos
        .map((photo: any) => {
          if (typeof photo === 'string') {
            return {uri: photo, isLocal: false};
          }
          if (typeof photo === 'object' && photo !== null) {
            return {
              id: photo.id,
              uri: photo.uri || '', // Ensure uri exists
              isLocal: photo.isLocal || false, // Default isLocal to false
            };
          }
          return null; // Or handle unexpected photo types
        })
        .filter(
          (p: any): p is Photo => p !== null && typeof p.uri === 'string',
        );
    }

    return data as Profile;
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch profile');
  }
});

// Helper function to upload a file (e.g., photo, avatar) to Supabase storage
const uploadFileToStorage = async (
  uri: string,
  userId: string,
  bucket: string,
  filePathPrefix: string = 'public', // e.g., 'avatars' or 'photos'
): Promise<string | null> => {
  if (!uri || !userId || !bucket) {
    return null;
  }

  // If URI is already a URL (already uploaded), return it directly
  if (uri.startsWith('http')) {
    return uri;
  }

  // For web, URI is likely a base64 data URL from FileReader
  if (!uri.startsWith('data:image')) {
    return null;
  }

  try {
    const response = await fetch(uri); // Convert data URI to blob
    const blob = await response.blob();

    if (!blob || blob.size === 0) {
      throw new Error('Empty file or blob from data URI');
    }

    const fileExtension = blob.type.split('/')[1] || 'jpg';
    const fileName = `${Date.now()}_${Math.random()
      .toString(36)
      .substring(2)}.${fileExtension}`;

    let fullFilePath;
    if (filePathPrefix && filePathPrefix.length > 0) {
      fullFilePath = `${filePathPrefix}/${userId}/${fileName}`;
    } else {
      fullFilePath = `${userId}/${fileName}`; // No leading slash when prefix is empty
    }

    const {data: uploadData, error: uploadError} = await supabase.storage
      .from(bucket)
      .upload(fullFilePath, blob, {
        contentType: blob.type,
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    // Use public URL for consistency with registration flow
    const {data: urlData} = supabase.storage
      .from(bucket)
      .getPublicUrl(fullFilePath);

    return urlData?.publicUrl || null;
  } catch (error) {
    return null;
  }
};

export const updateProfile = createAsyncThunk<
  Profile, // Return type
  Partial<Profile> & {id: string}, // Argument type
  {rejectValue: string}
>('profile/updateProfile', async (profileData, {rejectWithValue, getState}) => {
  const {id, photos, ...otherProfileData} = profileData;
  const sanitizedProfileData = normalizeUserContent(otherProfileData);

  const state = getState() as {auth: {user: {id: string} | null}};
  const userId = state.auth.user?.id;

  if (!userId || userId !== id) {
    return rejectWithValue('User ID mismatch or not authenticated.');
  }

  try {
    const updatePayload: Partial<Profile> = {
      ...sanitizedProfileData,
      updated_at: new Date().toISOString(),
    };

    // Only process and include photos if they were explicitly provided
    if (photos !== undefined) {
      let uploadedPhotos: Photo[] = [];
      if (photos.length > 0) {
        uploadedPhotos = await Promise.all(
          photos.map(async photo => {
            if (photo.isLocal && photo.uri.startsWith('data:image')) {
              const uploadedPhotoPath = await uploadFileToStorage(
                photo.uri,
                userId,
                'user-photos',
                '',
              );
              return {uri: uploadedPhotoPath || photo.uri, isLocal: false}; // Fallback to original URI if upload fails
            }
            return photo; // Return non-local or already uploaded photos as is
          }),
        );
        // Filter out any null URIs that might result from failed uploads if not handled by fallback
        uploadedPhotos = uploadedPhotos.filter(p => p.uri);
      }
      // Only include photos in update if they were provided
      updatePayload.photos = uploadedPhotos;
    }
    // If photos is undefined, don't include photos field in the update at all

    // Check if profile exists to determine create or update
    const {data: existingProfile, error: checkError} = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    let result;

    if (checkError) {
      // Handle check error but continue - we'll try to create/update anyway
    }

    if (!existingProfile) {
      // Create new profile
      result = await supabase
        .from('profiles')
        .insert({
          ...updatePayload,
          id: userId,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
    } else {
      // Update existing profile
      result = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', userId)
        .select()
        .single();
    }

    const {data, error} = result;

    if (error) {
      throw new Error(error.message);
    }

    if (data && data.photos && Array.isArray(data.photos)) {
      data.photos = data.photos
        .map((photo: any) => {
          if (typeof photo === 'string') {
            return {uri: photo, isLocal: false};
          }
          if (typeof photo === 'object' && photo !== null) {
            return {
              id: photo.id,
              uri: photo.uri || '',
              isLocal: photo.isLocal || false,
            };
          }
          return null;
        })
        .filter(
          (p: any): p is Photo => p !== null && typeof p.uri === 'string',
        );
    }

    return data as Profile;
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to update profile');
  }
});

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: state => {
      state.error = null;
    },
    setProfileData: (state, action: PayloadAction<Profile>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProfile.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchProfile.fulfilled,
        (state, action: PayloadAction<Profile>) => {
          state.data = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateProfile.fulfilled,
        (state, action: PayloadAction<Profile>) => {
          state.data = action.payload;
          state.loading = false;
        },
      )
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {clearProfileError, setProfileData} = profileSlice.actions;
export default profileSlice.reducer;
